import { IUserActivity } from "../../types/activity/Activity";
import {
  IAppointment,
  IAppointmentDetail,
} from "../../types/appointments/Appointment";
import {
  IAssociation,
  IAssociationResource,
} from "../../types/association/Association";
import { IAvailability } from "../../types/availabilities/Availabilities";
import { ICompetition } from "../../types/competition/Competition";
import { IGame } from "../../types/game/Game";
import { IGameConfiguration } from "../../types/game/GameConfiguration";
import { IImport } from "../../types/imports/import";
import { IPayRate } from "../../types/payrates/PayRate";
import { Role } from "../../types/persons/Role";
import { IPerson } from "../../types/persons/person";
import { Privilege } from "../../types/persons/privilege";
import { IPost } from "../../types/post/Post";
import {
  IProcessedSessionContainer,
  IShift,
  IShiftSession,
} from "../../types/shifts/Shift";
import { IShiftEnrolment } from "../../types/shifts/ShiftEnrolment";
import { ITeam } from "../../types/team/Team";
import { IVenue } from "../../types/venue/Venue";
import { IIntervalTree } from "../../util/IntervalTree";

export interface ILoadingState {
  loading?: boolean;
  loaded?: boolean;
  error?: string | null;
}

export interface IState {
  auth: IAuthState;
  posts: IPost[];
  associations: IAssociationState;
  shifts: IShiftState;
  shiftSessions: IShiftSessionState;
  shiftEnrolments: IShiftEnrolmentsState;
  venues: IVenueState;
  appointments: IAppointmentState;
  payrates: IPayRateState;
  persons: IPersonState;
  games: { [id: string]: IGame };
  gameConfigurations: IGameConfigurationState;
  availabilities: IAvailabilityState;
  imports: IImportsState;
  competitions: ICompetitionState;
  teams: ITeamState;
  activities: IActivityState;
  adminCommunications: IAdminCommunicationsState;
}

export interface IPersonState {
  self: string;
  map: { [id: string]: IPerson };
  byAssociation: { [associationId: string]: string[] };
}

export interface IAuthState {
  hasAuthenticated: null | boolean;
  loading: boolean;
  error: null | { message: null | string; code: null | string };

  associations: [];
  adminAssociation?: string;
  role: Role;
  privilege: Privilege;
}

export interface IAssociationState {
  admin: string[];
  map: { [key: string]: IAssociation };
  resources: { [key: string]: IAssociationResource[] };
}

export interface IShiftState {
  supervisable: string[];
  manageable: string[];

  map: { [shiftId: string]: IShift };
  loading: { [shiftId: string]: ILoadingState };
  filled: { [shiftSessionKey: string]: any };
}

export interface IShiftSessionState {
  map: { [sessionId: string]: IShiftSession };
  byShiftId: { [shiftId: string]: string[] };
  filled: { [sessionId: string]: IProcessedSessionContainer };
  loading: { [sessionKey: string]: ILoadingState };
}

export const getShiftEnrolmentKeySplit = (shift: string, person: string) =>
  `${shift}_${person}`;
export const getShiftEnrolmentKey = (enrolment: IShiftEnrolment) =>
  getShiftEnrolmentKeySplit(enrolment.shift, enrolment.person);
export interface IShiftEnrolmentsState {
  map: { [personshiftId: string]: IShiftEnrolment };
  byShiftId: { [shiftId: string]: string[] };
}

export interface IVenueState {
  map: { [venueId: string]: IVenue };
  byAssociation: { [associationId: string]: string[] };
}

export interface IAppointmentState {
  map: { [appointmentId: string]: IAppointment<IAppointmentDetail> };
  byPerson: { [personId: string]: string[] };
  byVenueDateCourt: { [gameKey: string]: { _id: string; position: string }[] };
}

export interface IPayRateState {
  map: { [payrateId: string]: IPayRate };
}

export interface IGameState { }

export interface IGameConfigurationState {
  map: { [id: string]: IGameConfiguration };
}

export interface IAvailabilityState {
  map: { [id: string]: IAvailability };
  byPerson: { [person: string]: string[] };
  tree: { [person: string]: IIntervalTree };
}

export interface IImportsState {
  map: { [importId: string]: IImport };
  // my imports
  self: string[];
}

export interface ITeamState {
  map: { [teamId: string]: ITeam };
}

export interface ICompetitionState {
  map: { [competitionId: string]: ICompetition };
}

export interface IActivityState {
  activities: IUserActivity<any>[];
}

export interface IAdminCommunicationsState {
  communications: any[];
  persons: { [id: string]: any };
  selectedId: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  filters: {
    mechanism?: number;
    communicationType?: string;
    startDate?: Date;
    endDate?: Date;
    search?: string;
  };
  loading: boolean;
  error: string | null;
}

export const InitialState: IState = {
  auth: {
    hasAuthenticated: null,
    adminAssociation: undefined,
    loading: true,
    error: null,

    associations: [],
    role: Role.none,
    privilege: 0,
  },

  availabilities: {
    map: {},
    byPerson: {},
    tree: {},
  },

  persons: {
    self: "",
    map: {},
    byAssociation: {},
  },

  posts: [],

  associations: {
    admin: [],
    map: {},
    resources: {},
  },

  shifts: {
    supervisable: [],
    manageable: [],
    map: {},
    loading: {},
    filled: {},
  },
  shiftSessions: {
    map: {},
    filled: {},
    byShiftId: {},
    loading: {},
  },
  shiftEnrolments: {
    map: {},
    byShiftId: {},
  },

  venues: {
    map: {},
    byAssociation: {},
  },
  appointments: {
    map: {},
    byPerson: {},
    byVenueDateCourt: {},
  },
  payrates: {
    map: {},
  },
  games: {},
  gameConfigurations: { map: {} },
  imports: {
    self: [],
    map: {},
  },
  competitions: {
    map: {},
  },
  teams: {
    map: {},
  },
  activities: {
    activities: [],
  },
  adminCommunications: {
    communications: [],
    persons: {},
    selectedId: null,
    pagination: {
      page: 1,
      limit: 50,
      total: 0,
      totalPages: 0,
    },
    filters: {},
    loading: false,
    error: null,
  },
};
